import { useEffect, useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from '@/lib/api';
import { Activity, Flame, Radio, TrendingUp, MapPin, Clock, Users, ArrowRight, Zap, Trophy } from 'lucide-react';

const SPORT_ICONS = {
  Football: '⚽', Cricket: '🏏', Badminton: '🏸', Tennis: '🎾', Basketball: '🏀',
  Hockey: '🏑', Volleyball: '🏐', Pickleball: '🏓', 'Table Tennis': '🏓',
  Squash: '🎾', Athletics: '🏃', Other: '🏟️',
};

const KIND_LABELS = {
  live: { text: 'LIVE NOW', color: '#10b981', dot: true },
  starting_soon: { text: 'STARTING SOON', color: '#f59e0b', dot: false },
  open_to_grab: { text: 'SLOT OPEN · JOIN', color: '#06b6d4', dot: false },
};

// The Live Now Engine — pulls real arenax_slots / arenax_bookings every 30 s and
// reflects network activity as social-proof + FOMO + activation surface.
export default function ArenaxLiveNow() {
  const [data, setData] = useState(null);
  const [city, setCity] = useState(null);
  const [error, setError] = useState(false);
  const tickRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get('/arenax/live-now');
      setData(r.data);
      setError(false);
      if (!city && r.data.default_city) setCity(r.data.default_city);
    } catch {
      setError(true);
    }
  };

  useEffect(() => {
    load();
    tickRef.current = setInterval(load, 30000);
    return () => clearInterval(tickRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cityRow = useMemo(() => (data?.cities || []).find(c => c.city === city) || (data?.cities || [])[0], [data, city]);
  const cityCards = useMemo(() => (data?.live_cards || []).filter(c => !city || c.city === city), [data, city]);

  if (error) {
    return (
      <section className="py-12 px-5 sm:px-8" data-testid="arenax-live-now-error">
        <div className="max-w-5xl mx-auto text-center text-slate-500 text-sm">Network signal momentarily unavailable.</div>
      </section>
    );
  }

  if (!data) {
    return (
      <section className="py-12 px-5 sm:px-8">
        <div className="max-w-5xl mx-auto text-center text-slate-500 text-sm">Connecting to ArenaX network…</div>
      </section>
    );
  }

  const network = data.network || {};
  const isAlive = (network.live_now || 0) + (network.starting_soon || 0) + (network.open_to_grab || 0) > 0;

  return (
    <section className="relative py-20 px-5 sm:px-8 border-t border-cyan-500/10" data-testid="arenax-live-now">
      <div className="max-w-7xl mx-auto">
        {/* HEADER */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="relative">
                <Radio className="h-4 w-4 text-emerald-400" />
                <div className="absolute inset-0 animate-ping">
                  <Radio className="h-4 w-4 text-emerald-400 opacity-50" />
                </div>
              </div>
              <span className="text-[11px] tracking-[0.4em] text-emerald-400 font-bold">LIVE · ARENAX NETWORK</span>
            </div>
            <h2 className="text-3xl sm:text-5xl font-black text-slate-100 leading-[1.05]">
              Right now,
              <span className="block" style={{ background: 'linear-gradient(120deg, #10b981 0%, #22d3ee 50%, #f59e0b 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                games are happening.
              </span>
            </h2>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Activity className="h-3.5 w-3.5 text-emerald-400 animate-pulse" />
            <span>Refreshes every 30s · last sync {new Date(data.now_iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        </div>

        {/* NETWORK PULSE BAND */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <PulseStat icon={Flame} label="Live now" value={network.live_now || 0} color="#10b981" pulse={(network.live_now || 0) > 0} testid="arenax-live-stat-live" />
          <PulseStat icon={Clock} label="Starting in 60min" value={network.starting_soon || 0} color="#f59e0b" pulse={(network.starting_soon || 0) > 0} testid="arenax-live-stat-soon" />
          <PulseStat icon={Zap} label="Slots open to grab" value={network.open_to_grab || 0} color="#06b6d4" pulse={(network.open_to_grab || 0) > 0} testid="arenax-live-stat-open" />
          <PulseStat icon={MapPin} label="Active venues" value={network.total_active_venues || 0} color="#a855f7" pulse={false} testid="arenax-live-stat-venues" />
        </div>

        {/* CITY TABS */}
        {(data.cities || []).length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6" data-testid="arenax-live-city-tabs">
            {data.cities.map(c => (
              <button key={c.city} onClick={() => setCity(c.city)} data-testid={`arenax-live-city-${c.city.toLowerCase()}`}
                className={`px-4 py-2 rounded-xl text-xs font-bold tracking-wide transition-all flex items-center gap-2 ${
                  city === c.city
                    ? 'text-slate-950 shadow-lg'
                    : 'bg-slate-900/60 text-slate-300 hover:bg-slate-800 border border-slate-800'
                }`}
                style={city === c.city ? { background: 'var(--ax-primary-gradient)' } : {}}>
                <MapPin className="h-3 w-3" />
                {c.city}
                <span className="text-[10px] opacity-80">{c.live_now_count} live</span>
              </button>
            ))}
          </div>
        )}

        {/* MAIN GRID */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* LEFT: City Occupancy + Sport Breakdown + Trending */}
          <div className="lg:col-span-5 space-y-4">
            {cityRow && <OccupancyPanel city={cityRow} />}
            {data.trending && <TrendingPanel trending={data.trending} />}
          </div>

          {/* RIGHT: Live Game Cards */}
          <div className="lg:col-span-7">
            <div className="rounded-2xl border border-slate-800 p-5 backdrop-blur-sm" style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.05), var(--ax-card-bg-soft))' }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-slate-100">Live Game Feed</h3>
                <span className="text-[10px] text-slate-500 tracking-wider">{cityCards.length} ACTIVITY</span>
              </div>
              {cityCards.length === 0 ? (
                <div className="text-center py-12">
                  <Activity className="h-8 w-8 text-slate-600 mx-auto mb-3" />
                  <p className="text-sm text-slate-400 font-semibold mb-1">No live activity in {city || 'this city'} right now</p>
                  <p className="text-xs text-slate-600">Be the first to book a slot — and you'll appear here.</p>
                </div>
              ) : (
                <div className="space-y-2.5 max-h-[520px] overflow-y-auto pr-1" data-testid="arenax-live-cards">
                  <AnimatePresence mode="popLayout">
                    {cityCards.map((c, idx) => (
                      <motion.div
                        key={c.id}
                        layout
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.3, delay: idx * 0.04 }}>
                        <LiveGameCard card={c} />
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* CALL TO ACTION */}
        {isAlive && (
          <div className="mt-8 rounded-2xl p-5 border border-cyan-500/20 flex flex-wrap items-center justify-between gap-3"
            style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.08), rgba(245,158,11,0.06))' }}>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-emerald-500/20 border border-emerald-500/40">
                <Users className="h-5 w-5 text-emerald-300" />
              </div>
              <div>
                <div className="text-sm font-bold text-slate-100">Games are starting near you</div>
                <div className="text-xs text-slate-400">{(network.starting_soon || 0) + (network.open_to_grab || 0)} sessions in the next hour. One tap to join.</div>
              </div>
            </div>
            <Link to="/arenax/venues" data-testid="arenax-live-cta-join">
              <button className="px-5 py-2.5 rounded-xl font-bold text-slate-950 text-sm flex items-center gap-2"
                style={{ background: 'var(--ax-primary-gradient)' }}>
                Find Your Game <ArrowRight className="h-4 w-4" />
              </button>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}

function PulseStat({ icon: Icon, label, value, color, pulse, testid }) {
  return (
    <div className="rounded-2xl p-4 border border-slate-800 relative overflow-hidden backdrop-blur-sm" style={{ background: 'var(--ax-card-bg-soft)' }} data-testid={testid}>
      {pulse && (
        <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-30 animate-pulse"
          style={{ background: `radial-gradient(circle, ${color}, transparent 70%)` }} />
      )}
      <div className="flex items-center gap-2 mb-1.5 relative z-10">
        <Icon className="h-3.5 w-3.5" style={{ color }} />
        <span className="text-[10px] tracking-[0.2em] text-slate-500 uppercase font-semibold">{label}</span>
      </div>
      <div className="text-3xl font-black relative z-10" style={{ color }}>{value}</div>
    </div>
  );
}

function OccupancyPanel({ city }) {
  return (
    <div className="rounded-2xl p-5 border border-slate-800 backdrop-blur-sm" style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.05), var(--ax-card-bg-soft))' }} data-testid="arenax-live-occupancy">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-cyan-300" />
          <span className="text-sm font-bold text-slate-100">{city.city}</span>
        </div>
        <span className="text-[10px] tracking-wider text-slate-500">{city.venues_count} venues</span>
      </div>
      {/* Big occupancy bar */}
      <div className="mb-1.5 flex items-end justify-between">
        <span className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Network occupancy today</span>
        <span className="text-2xl font-black text-cyan-300">{city.occupancy_pct}%</span>
      </div>
      <div className="h-2.5 rounded-full bg-slate-800 overflow-hidden mb-5">
        <motion.div initial={{ width: 0 }} animate={{ width: `${city.occupancy_pct}%` }} transition={{ duration: 1.2, ease: 'easeOut' }}
          className="h-full rounded-full"
          style={{ background: 'var(--ax-primary-gradient)' }} />
      </div>
      {/* Sport breakdown */}
      <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold mb-2">Active sports</div>
      <div className="space-y-1.5">
        {city.active_sports.slice(0, 5).map(([sport, n]) => {
          const max = city.active_sports[0][1] || 1;
          const pct = (n / max) * 100;
          return (
            <div key={sport} className="flex items-center gap-2">
              <span className="text-base">{SPORT_ICONS[sport] || '🏟️'}</span>
              <span className="text-xs text-slate-300 font-semibold w-20">{sport}</span>
              <div className="flex-1 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.7 }}
                  className="h-full rounded-full"
                  style={{ background: 'linear-gradient(90deg, #06b6d4, #22d3ee)' }} />
              </div>
              <span className="text-xs text-cyan-300 font-bold tabular-nums w-10 text-right">{n}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TrendingPanel({ trending }) {
  return (
    <div className="rounded-2xl p-5 border border-amber-500/20 backdrop-blur-sm"
      style={{ background: 'linear-gradient(160deg, rgba(245,158,11,0.06), var(--ax-card-bg-soft))' }}
      data-testid="arenax-live-trending">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="h-4 w-4 text-amber-300" />
        <span className="text-sm font-bold text-slate-100">Trending Today</span>
      </div>
      {trending.top_venue && (
        <Link to={`/arenax/venues/${trending.top_venue.venue_slug || trending.top_venue.venue_id}`} className="block mb-3 p-3 rounded-xl bg-slate-900/50 border border-slate-800 hover:border-amber-500/40 transition-colors" data-testid="arenax-live-trending-venue">
          <div className="text-[10px] tracking-[0.2em] uppercase text-amber-300 font-bold mb-1">🔥 Top Venue Today</div>
          <div className="text-sm font-bold text-slate-100">{trending.top_venue.venue_name}</div>
          <div className="text-xs text-slate-500">{trending.top_venue.bookings_today} bookings · ₹{trending.top_venue.gmv_today?.toFixed(0)} GMV</div>
        </Link>
      )}
      {trending.fastest_filling_sport && (
        <div className="mb-3 p-3 rounded-xl bg-slate-900/50 border border-slate-800">
          <div className="text-[10px] tracking-[0.2em] uppercase text-emerald-400 font-bold mb-1">⚡ Filling Fast</div>
          <div className="text-sm font-bold text-slate-100">{trending.fastest_filling_sport.sport}</div>
          <div className="text-xs text-slate-500">{trending.fastest_filling_sport.fill_pct}% of slots booked · {trending.fastest_filling_sport.booked}/{trending.fastest_filling_sport.total}</div>
        </div>
      )}
      {trending.peak_time && (
        <div className="p-3 rounded-xl bg-slate-900/50 border border-slate-800">
          <div className="text-[10px] tracking-[0.2em] uppercase text-cyan-300 font-bold mb-1">⏰ Peak Slot</div>
          <div className="text-sm font-bold text-slate-100">{trending.peak_time}</div>
          <div className="text-xs text-slate-500">Most booked start time across the network</div>
        </div>
      )}
    </div>
  );
}

function LiveGameCard({ card }) {
  const meta = KIND_LABELS[card.kind] || KIND_LABELS.live;
  return (
    <Link to={`/arenax/venues/${card.venue_slug || card.venue_id}`}
      className="block rounded-xl border border-slate-800 hover:border-cyan-500/40 p-3 transition-all hover:-translate-y-0.5 group"
      style={{ background: 'linear-gradient(140deg, var(--ax-card-bg-soft), rgba(8,145,178,0.04))' }}
      data-testid={`arenax-live-card-${card.id}`}>
      <div className="flex items-start gap-3">
        {/* Sport icon block */}
        <div className="h-12 w-12 rounded-xl flex items-center justify-center text-2xl shrink-0 relative"
          style={{ background: card.cover_image_url ? `url(${card.cover_image_url}) center/cover` : 'linear-gradient(135deg, rgba(6,182,212,0.2), rgba(245,158,11,0.2))' }}>
          <span className="absolute inset-0 rounded-xl bg-slate-950/50 flex items-center justify-center">{SPORT_ICONS[card.sport] || '🏟️'}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            {meta.dot && <span className="relative h-2 w-2"><span className="absolute inset-0 rounded-full" style={{ background: meta.color }} /><span className="absolute inset-0 rounded-full animate-ping" style={{ background: meta.color }} /></span>}
            <span className="text-[9px] tracking-[0.2em] font-bold" style={{ color: meta.color }}>{meta.text}</span>
          </div>
          <div className="text-sm font-bold text-slate-100 truncate group-hover:text-cyan-300 transition-colors">
            {card.sport} · {card.venue_name}
          </div>
          <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
            <span>{card.court_name}</span>
            <span>·</span>
            <span>{card.city}</span>
            <span>·</span>
            <Clock className="h-3 w-3" />
            <span>{card.start_time}{card.kind !== 'live' && card.minutes_to_start > 0 && ` (in ${card.minutes_to_start}m)`}</span>
          </div>
        </div>
        {card.kind === 'open_to_grab' && (
          <div className="text-right shrink-0">
            <div className="text-sm font-black text-cyan-300">₹{card.price}</div>
            <div className="text-[10px] text-slate-500">grab now</div>
          </div>
        )}
      </div>
    </Link>
  );
}
